import { ButtonPrimary, Type } from '@spotify-internal/encore-web';
import React, { Fragment, useEffect, useState } from 'react';
import { default as ReCAPTCHA } from 'react-google-recaptcha';

import withTranslation from 'lib/shared/i18n/withTranslation';
import { TranslationProps } from 'etc/types';
import Step2ReportedContentUriAndType from 'components/ContentReporting/ContentPolicyReporting/Step2ReportedContentUriAndType';
import { StepProps } from 'containers/ContentReporting/ContentPolicyReporting/utils/types';
import analytics, {
  StepLabel,
} from 'components/ContentReporting/ContentPolicyReporting/policy-reporting-analytics-utils';
import {
  EXTERNAL_ACCEPTED_URI_TYPES,
  EXTERNAL_CONTENT_URI_TO_CONTENT_TYPE_INPUT_ACCEPTED_CONTENT_TYPES,
  Field,
} from 'components/ContentReporting/ContentPolicyReporting/content-policy-reporting-config';
import { CONTENTREPORTING_GOOGLE_RECAPTCHA_V2_SITE_KEY } from 'lib/shared/config/config';

export interface Step2ExternalReportedContentProps extends StepProps, TranslationProps {
  onCaptchaChange: (token: string | null) => void;
  fieldValue: (fieldIdentifier: string) => string;
  fieldValid: (fieldIdentifier: string) => boolean;
}

const Step2ExternalReportedContent = ({
  forceValidation,
  loading,
  setLoading,
  location,
  t,
  onStepSubmit,
  onFieldChange,
  onCaptchaChange,
  fieldValue,
  fieldValid,
}: Step2ExternalReportedContentProps): JSX.Element => {
  useEffect(
    () => analytics.trackVirtualPageView(location.pathname, location.search, StepLabel.Step2ReportedContentAllUsers),
    [location.pathname, location.search],
  );

  const captchaRef = React.createRef<ReCAPTCHA>();
  const [captchaLoaded, setCaptchaLoaded] = useState(false);

  return (
    <Fragment>
      <Type.h1 paddingBottom="0.67em" variant="altoBrio">
        {t('what_do_you_want_to_report')}
      </Type.h1>
      <Step2ReportedContentUriAndType
        acceptedUriTypes={EXTERNAL_ACCEPTED_URI_TYPES}
        uriToContentTypeMappings={EXTERNAL_CONTENT_URI_TO_CONTENT_TYPE_INPUT_ACCEPTED_CONTENT_TYPES}
        forceValidation={forceValidation}
        onFieldChange={onFieldChange}
        fieldValue={fieldValue}
        fieldValid={fieldValid}
        setLoading={setLoading}
      />

      <ReCAPTCHA
        sitekey={CONTENTREPORTING_GOOGLE_RECAPTCHA_V2_SITE_KEY}
        size="invisible"
        theme="dark"
        onChange={onCaptchaChange}
        ref={captchaRef}
        onExpired={() => onCaptchaChange('')}
        asyncScriptOnLoad={() => setCaptchaLoaded(true)}
      />

      <ButtonPrimary
        disabled={loading && !captchaLoaded}
        onClick={() => {
          const recaptchaTokenInFormData = fieldValue(Field.VERIFICATION_CAPTCHA_TOKEN);
          const recaptchaTokenValid = fieldValid(Field.VERIFICATION_CAPTCHA_TOKEN);

          if (recaptchaTokenInFormData && recaptchaTokenValid) {
            onStepSubmit();
          } else {
            captchaRef.current?.executeAsync().then(() => {
              onStepSubmit();
            });
          }
        }}
      >
        {t('submit')}
      </ButtonPrimary>
    </Fragment>
  );
};

export default withTranslation('content_policy_reporting')(Step2ExternalReportedContent);
